import React from 'react';
import { Link } from 'gatsby';
import { ArrowRight } from 'lucide-react';

type Variant =
  | 'working-session'
  | 'diagnostic'
  | 'training'
  | 'pillar-essay'
  | 'case-study'
  | 'service';

interface CTASectionProps {
  /** Which CTA pattern to render. */
  variant: Variant;
  /** Optional override for the headline. */
  headline?: string;
  /** Optional override for the description copy. */
  description?: string;
  /** Optional override for the primary button text. */
  primaryText?: string;
  /** Optional override for the primary button destination. */
  primaryLink?: string;
  /** Optional secondary CTA — defaults to a relevant alternative per variant. */
  secondaryText?: string;
  secondaryLink?: string;
  /** Visual surface — `dark` for dark backgrounds, `light` for light. Default `dark`. */
  surface?: 'dark' | 'light';
  /** Source identifier for utm attribution. */
  source?: string;
}

const VARIANT_DEFAULTS: Record<
  Variant,
  {
    headline: string;
    description: string;
    primaryText: string;
    primaryLink: string;
    secondaryText: string;
    secondaryLink: string;
  }
> = {
  'working-session': {
    headline: 'Ready for a real conversation?',
    description:
      'Book a 90-minute executive working session with a senior practitioner. No deck. No pitch. We use the time to understand your operating model, the binding constraints, and which engagement is the right one to start with.',
    primaryText: 'Book a working session',
    primaryLink: '/contact',
    secondaryText: 'Take the maturity diagnostic first',
    secondaryLink: '/ai-enablement-maturity',
  },
  diagnostic: {
    headline: 'Score your own organisation',
    description:
      '25 questions across the five enablement pillars (production function, data layer, decision systems, operating model, governance). Per-pillar breakdown and prioritised next steps in five minutes — results render immediately on screen.',
    primaryText: 'Take the diagnostic',
    primaryLink: '/ai-enablement-maturity',
    secondaryText: 'Or explore the AI Enablement service',
    secondaryLink: '/services/ai-enablement',
  },
  training: {
    headline: 'Eight free certification courses',
    description:
      'Self-paced courses for COOs, CROs, CTOs, and model risk leaders — AI enablement, governance, data foundations, BPMN, Six Sigma, change management, and operating model design. No signup required to start.',
    primaryText: 'Browse all courses',
    primaryLink: '/training',
    secondaryText: 'Or take the AI Governance course',
    secondaryLink: '/training/ai-governance-model-risk',
  },
  'pillar-essay': {
    headline: 'Read the pillar essay',
    description:
      'For the structural argument behind the work — what AI enablement actually means, why most pilots do not compound, and what changes when you redesign the operating model around AI as a native capability.',
    primaryText: 'Read the essay',
    primaryLink: '/blog/what-ai-enablement-really-means',
    secondaryText: 'See all insights',
    secondaryLink: '/blog',
  },
  'case-study': {
    headline: 'See what the work looks like',
    description:
      'Anonymised case studies from FTSE 100 banks, insurers, and asset managers. Real challenges, real structural redesigns, and the outcomes that landed.',
    primaryText: 'Read the case studies',
    primaryLink: '/case-studies',
    secondaryText: 'Or book a working session',
    secondaryLink: '/contact',
  },
  service: {
    headline: 'Explore the AI Enablement service',
    description:
      'Our flagship engagement — five enablement pillars, embedded governance, sequenced delivery. Sector variants for banking, insurance, asset management, wealth, capital markets, payments, healthcare, life sciences, energy, and public sector.',
    primaryText: 'Explore the service',
    primaryLink: '/services/ai-enablement',
    secondaryText: 'See all services',
    secondaryLink: '/services',
  },
};

const CTASection: React.FC<CTASectionProps> = ({
  variant,
  headline,
  description,
  primaryText,
  primaryLink,
  secondaryText,
  secondaryLink,
  surface = 'dark',
  source,
}) => {
  const defaults = VARIANT_DEFAULTS[variant];
  const utm = source
    ? `?utm_source=${source}&utm_medium=cta-section&utm_content=${variant}`
    : '';
  const finalPrimary = (primaryLink || defaults.primaryLink) + (primaryLink ? '' : utm);
  const finalSecondary = secondaryLink || defaults.secondaryLink;

  if (surface === 'light') {
    return (
      <section className="not-prose my-12 bg-gradient-to-br from-gray-50 via-white to-gray-50 border border-gray-200 rounded-3xl p-8 md:p-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-primary-900 mb-4 leading-tight">
            {headline || defaults.headline}
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            {description || defaults.description}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to={finalPrimary}
              className="inline-flex items-center gap-2 bg-primary-900 hover:bg-primary-800 text-white font-bold py-4 px-7 rounded-xl transition-all shadow-lg hover:-translate-y-0.5"
            >
              {primaryText || defaults.primaryText}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={finalSecondary}
              className="inline-flex items-center gap-2 bg-white text-primary-900 border border-gray-200 hover:border-accent-500 font-bold py-4 px-7 rounded-xl transition-all"
            >
              {secondaryText || defaults.secondaryText}
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Dark surface (default)
  return (
    <section className="not-prose my-12 relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white p-10 md:p-14 border border-primary-700">
      <div className="absolute inset-0 opacity-20 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent-500 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyber-500 rounded-full blur-3xl" />
      </div>
      <div className="relative max-w-3xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-white mb-4 leading-tight">
          {headline || defaults.headline}
        </h2>
        <p className="text-lg text-white/80 mb-8 leading-relaxed">
          {description || defaults.description}
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Link
            to={finalPrimary}
            className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-400 text-primary-900 font-bold py-4 px-7 rounded-xl transition-all shadow-lg hover:-translate-y-0.5"
          >
            {primaryText || defaults.primaryText}
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to={finalSecondary}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white font-bold py-4 px-7 rounded-xl transition-all"
          >
            {secondaryText || defaults.secondaryText}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
